import React, { useCallback, useEffect, useState } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { Layout, Button, Flex } from "antd"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import { isEqual, omit } from "lodash"
import useSearchFilter from "hooks/useSearchFilter"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./Calendar.module.css"
import Header from "./Header"
import CalendarViewSwitcher from "./CalendarViewSwitcher"
import { getCalendarBookings } from "store/calendar"
import { fetchExperiences } from "store/experiences"
import { getCalendarCrew } from "store/staff"
import { ExitScheduleTimeOff, Journal, ScheduleTimeOff } from "./Modals"
import { useCalendarDateRange } from "./useCalendarDateRange"
import { useDevice } from "hooks/useDevice"
import { useMediaQuery } from "react-responsive"
import { useTranslation } from "react-i18next"
import { routes } from "router"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import CustomDrawer from "ui/Drawer"
function Calendar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isMobile, isTablet, isDesktop } = useDevice()
  const isWideDesktop = useMediaQuery({ query: "(min-width: 1280px)" })
  const { t } = useTranslation()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false)
  const [calendarFilters, setCalendarFilters] = useState({})
  const [params, updateSearchFilters] = useSearchFilter({
    accessAttributes: ["range", "date", "agenda", "weather", "trip_id", "journal_date", "unavailable_id", "tab"]
  })
  const [paramsState, setParamsState] = useState(params)

  // Keep paramsState in sync with params only when they actually differ
  useEffect(() => {
    if (!isEqual(paramsState, params)) {
      setParamsState(params)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)])

  // Ensure default to month view, correct date, and tab in URL
  useEffect(() => {
    const today = moment().format(DATE_FORMAT);
    let changed = false;
    const newParams = { ...params };
    if (!params.range) {
      newParams.range = "month";
      changed = true;
    }
    if (!params.date) {
      newParams.date = today;
      changed = true;
    }
    if (!params.tab) {
      newParams.tab = "all-bookings";
      changed = true;
    }
    if (changed) {
      updateSearchFilters(newParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // (Removed unused calendarDate variable)
  // Consolidated modal state for maintainability
  const [modals, setModals] = useState({ scheduleTimeOff: false, exitScheduleTimeOff: false, journal: false })
  // Preserve existing setter APIs via thin wrappers
  const setIsScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, scheduleTimeOff: open }))
  const setIsExitScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, exitScheduleTimeOff: open }))
  const setIsJournalModalOpen = (open) => setModals((m) => ({ ...m, journal: open }))

  const dates = useCalendarDateRange(params)

  useEffect(() => {
    dispatch(getCalendarCrew())
    dispatch(fetchExperiences("calendar"))
  }, [dispatch])

  const changeHandler = useCallback(
    ({ target, extra }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value,
        ...(extra && { [extra.name]: extra.value })
      })),
    []
  )

  useEffect(() => {
    // Only update search filters if paramsState differs from params (ignoring journal_date, unavailable_id)
    if (!isEqual(paramsState, omit(params, ["journal_date", "unavailable_id"]))) {
      updateSearchFilters(paramsState)
    }
  }, [paramsState, updateSearchFilters, params])

  useEffect(() => {
    if (!dates?.start || !dates?.end) return

    const apiParams = new URLSearchParams()

    // Day view sends `date`, week/month sends `start_date` + `end_date`
    if (params.range === "date") {
      apiParams.set("date", dates.start)
    } else {
      apiParams.set("start_date", dates.start)
      apiParams.set("end_date", dates.end)
    }

    if (params.tab === "my-bookings") {
      apiParams.set("view_type", "my_bookings")
    }

    if (calendarFilters.search) {
      apiParams.set("search", calendarFilters.search)
    }

    // Array filters — profile_ids[]=220&profile_ids[]=215
    if (calendarFilters.profile_ids?.length) {
      calendarFilters.profile_ids.forEach((id) => apiParams.append("profile_ids[]", id))
    }
    if (calendarFilters.boat_ids?.length) {
      calendarFilters.boat_ids.forEach((id) => apiParams.append("boat_ids[]", id))
    }
    if (calendarFilters.category_ids?.length) {
      calendarFilters.category_ids.forEach((id) => apiParams.append("category_ids[]", id))
    }
    if (calendarFilters.experience_ids?.length) {
      calendarFilters.experience_ids.forEach((id) => apiParams.append("experience_ids[]", id))
    }
    if (calendarFilters.role_ids?.length) {
      calendarFilters.role_ids.forEach((id) => apiParams.append("role_ids[]", id))
    }
    if (calendarFilters.unassigned) {
      apiParams.set("unassigned", "true")
    }

    dispatch(getCalendarBookings(apiParams))
  }, [dates?.start, dates?.end, params.range, params.tab, calendarFilters, dispatch])


  useEffect(() => {
    if (!dates?.start || !dates?.end) return
    const data = {}
    const startMoment = moment(dates.start)
    const endMoment = moment(dates.end)

    if (params.trip_id) data.trip_id = params.trip_id

    data.forecast_date = []
    while (startMoment.isSameOrBefore(endMoment)) {
      data.forecast_date.push(startMoment.format(DATE_FORMAT))
      startMoment.add(1, "day")
    }
  }, [dates?.start, dates?.end, params?.trip_id, params?.weather, dispatch])

  const handleCancelScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(true)
  }
  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleCancelExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
  }

  const handleYesExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
    setIsScheduleTimeOffModalOpen(false)
  }

  const handleJournalClick = () => {
    setIsJournalModalOpen(true)
  }

  return (
    <>
      <Layout className={styles.container} id="dashboard">
        <div className={styles.header}>
          <Header
            params={params}
            onChangeFilters={changeHandler}
            handleJournalClick={handleJournalClick}
            setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
            onFiltersChange={setCalendarFilters}
          />
        </div>
        <div className={styles.calendarWrapper}>
          <div className={styles.calendar}>
            {(isMobile || isTablet) && (
              <div className={styles.mobileGoToToday}>
                <Button
                  color="primary"
                  type="default"
                  variant="outlined"
                  className={styles.goToToday}
                  onClick={() => {
                    const today = moment().format(DATE_FORMAT)
                    changeHandler({ target: { name: "date", value: today }, extra: { name: "range", value: "date" } })
                  }}
                >
                  Go to Today
                </Button>
              </div>
            )}
            <CalendarViewSwitcher
              onChangeFilters={changeHandler}
              experienceIds={calendarFilters.experience_ids || []}
              crewIds={[
                ...(calendarFilters.unassigned ? ["unassigned"] : []),
                ...(calendarFilters.profile_ids || [])
              ]}
              equipmentIds={calendarFilters.boat_ids || []}
              categoryIds={calendarFilters.category_ids || []}
              roleIds={calendarFilters.role_ids || []}
            />
          </div>
        </div>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </Layout>
      {modals.journal && (
        <Journal
          open={modals.journal}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={params.date || moment().format(DATE_FORMAT)}
          startDate={dates?.start || params.date || moment().format(DATE_FORMAT)}
          endDate={dates?.end || params.date || moment().format(DATE_FORMAT)}
          range={params.range}
        />
      )}
      {modals.scheduleTimeOff && (
        <ScheduleTimeOff
          dates={dates}
          open={modals.scheduleTimeOff}
          onCancel={handleCancelScheduleTimeOffModal}
          setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
        />
      )}
      {modals.exitScheduleTimeOff && (
        <ExitScheduleTimeOff
          open={modals.exitScheduleTimeOff}
          onCancel={handleCancelExitScheduleTimeOffModal}
          onYes={handleYesExitScheduleTimeOffModal}
        />
      )}
      {(isMobile || isTablet || (isDesktop && !isWideDesktop)) && (
        <>
          <div className={styles.fab}>
            <button
              type="button"
              className={styles.fabButton}
              onClick={() => setActionsDrawerOpen(true)}
              aria-label="Actions"
            >
              +
            </button>
          </div>
          <CustomDrawer open={actionsDrawerOpen} setOpen={setActionsDrawerOpen} title="Actions">
            <Flex vertical gap={12}>
              {canCreateBooking && (
                <Button
                  block
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => { setActionsDrawerOpen(false); if (active) navigate(routes.guideBookingNewPath()); }}
                >
                  {t("bookings.add")}
                </Button>
              )}
              {(params.tab === 'my-bookings' || params.tab === 'crew') && (
                <Button
                  block
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => { setActionsDrawerOpen(false); if (active) setIsScheduleTimeOffModalOpen(true); }}
                >
                  {t("unavailable_days.schedule_time_off")}
                </Button>
              )}
              {canCreateJournal && (
                <Button
                  block
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => { setActionsDrawerOpen(false); if (active) handleJournalClick(); }}
                >
                  {t("calendar.journal.add_journal")}
                </Button>
              )}
            </Flex>
          </CustomDrawer>
        </>
      )}
    </>
  )
}

export default Calendar
